import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from "@/utils";
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import { 
  User, 
  LogOut, 
  ChevronRight, 
  Shield,
  Users,
  Bell,
  Heart,
  Camera,
  Edit,
  Music,
  Calendar,
  Baby,
  Handshake,
  BookOpen,
  Chrome,
  Trash2,
  ClipboardList,
  CreditCard,
  Trophy
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getMemberByUser } from '@/lib/memberLookup';
import { getOAuthRedirectUrl, getRecoveryRedirectUrl } from '@/lib/authRecovery';
import { useAuth } from '@/lib/AuthContext';
import { RANKING_TEAMS } from '@/lib/rankingMBV';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmailAddress = (value) => {
  const email = String(value || '').trim().toLowerCase();
  if (!email || email.length > 254) return false;
  if (!EMAIL_REGEX.test(email)) return false;
  if (email.includes('..')) return false;
  return true;
};

const isNativePlatform = () => {
  if (typeof window === 'undefined') return false;
  if (typeof window.Capacitor?.isNativePlatform === 'function') {
    return window.Capacitor.isNativePlatform();
  }
  return false;
};

const getNativePlatform = () => {
  if (!isNativePlatform()) return 'web';
  return String(window.Capacitor?.getPlatform?.() || '').toLowerCase() || 'native';
};

export default function Perfil() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser, isAuthenticated: isLoggedIn, isLoadingAuth: checkingAuth } = useAuth();
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [socialSubmitting, setSocialSubmitting] = useState('');
  const [profileLoadTimedOut, setProfileLoadTimedOut] = useState(false);
  const [resolvedMemberData, setResolvedMemberData] = useState(null);
  const [deleteAccountSubmitting, setDeleteAccountSubmitting] = useState(false);
  const [lastNativeAuthError, setLastNativeAuthError] = useState('');
  const nativePlatform = getNativePlatform();
  const showAppleLogin = nativePlatform === 'web' || nativePlatform === 'ios';
  const isRecoveryUrl = () => {
    if (typeof window === 'undefined') return false;
    const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    const queryParams = new URLSearchParams(window.location.search || '');
    const isRecoveryType = hashParams.get('type') === 'recovery' || queryParams.get('type') === 'recovery';
    const isUpdateMode = queryParams.get('mode') === 'update';
    return isRecoveryType || isUpdateMode;
  };

  useEffect(() => {
    if (isRecoveryUrl()) {
      setAuthMode('update');
    }
  }, []);

  const { data: memberData, isLoading: loadingMemberData } = useQuery({
    queryKey: ['memberData', currentUser?.id, currentUser?.email],
    queryFn: () => getMemberByUser(currentUser),
    enabled: !!currentUser,
    retry: false,
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    if (memberData) {
      setResolvedMemberData(memberData);
      return;
    }

    if (!isLoggedIn) {
      setResolvedMemberData(null);
    }
  }, [memberData, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;
    setSocialSubmitting('');
    setAuthSubmitting(false);
  }, [isLoggedIn]);

  useEffect(() => {
    try {
      const rawValue = window.localStorage.getItem('mbv-last-auth-debug');
      const parsedValue = rawValue ? JSON.parse(rawValue) : null;
      if (parsedValue?.stage && parsedValue.stage !== 'success' && parsedValue?.message) {
        setLastNativeAuthError(parsedValue.message);
      }
    } catch (_error) {
      setLastNativeAuthError('');
    }

    const handleAuthCallback = (event) => {
      setSocialSubmitting('');
      const detail = event?.detail || {};
      if (detail.success === false && detail.error) {
        setLastNativeAuthError(detail.error);
        toast.error(detail.error);
        return;
      }

      if (detail.success) {
        setLastNativeAuthError('');
        try {
          window.localStorage.removeItem('mbv-last-auth-debug');
        } catch (_error) {
          // Ignore storage cleanup failures.
        }
      }
    };

    window.addEventListener('mbv-auth-callback', handleAuthCallback);
    return () => window.removeEventListener('mbv-auth-callback', handleAuthCallback);
  }, []);

  const { data: permissions } = useQuery({
    queryKey: ['permissions', currentUser?.email],
    queryFn: async () => {
      if (!currentUser?.email) return null;
      const perms = await base44.entities.AdminPermission.filter({ user_email: currentUser.email });
      return perms[0] || null;
    },
    enabled: !!currentUser?.email
  });

  const { data: rankingMemberships = [] } = useQuery({
    queryKey: ['rankingMemberships', currentUser?.email, memberData?.id],
    queryFn: async () => {
      if (!currentUser?.email && !memberData?.id) return [];
      const checks = await Promise.all(RANKING_TEAMS.map(async (team) => {
        const byMemberId = memberData?.id
          ? await base44.entities[team.memberEntity].filter({ member_id: memberData.id }).catch(() => [])
          : [];
        if (byMemberId.length > 0) return team.key;

        const byEmail = currentUser?.email
          ? await base44.entities[team.memberEntity].filter({ email: currentUser.email }).catch(() => [])
          : [];
        return byEmail.length > 0 ? team.key : null;
      }));
      return checks.filter(Boolean);
    },
    enabled: !!currentUser && (!!currentUser.email || !!memberData?.id)
  });

  const isAdmin = currentUser?.role === 'admin' || permissions?.is_super_admin || permissions?.can_manage_members || permissions?.can_manage_events || permissions?.can_manage_groups || permissions?.can_manage_communication || permissions?.can_manage_gallery || permissions?.can_manage_banners || permissions?.can_manage_registrations || permissions?.can_moderate_reviews || permissions?.can_manage_church_info;
  const hasRankingMBVAccess = rankingMemberships.length > 0;

  useEffect(() => {
    if (!isLoggedIn || !loadingMemberData || resolvedMemberData) {
      setProfileLoadTimedOut(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setProfileLoadTimedOut(true);
    }, 10000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isLoggedIn, loadingMemberData, resolvedMemberData]);

  const handleLogout = () => {
    base44.auth.logout(window.location.href);
  };

  const getAccountDeletionUrl = () => {
    if (typeof window === 'undefined') return '/eliminacao-de-conta.html';
    return new URL('/eliminacao-de-conta.html', window.location.origin).toString();
  };

  const handleAccountDeletionRequest = async () => {
    if (!currentUser?.email) {
      toast.error('Nao foi possivel identificar o e-mail da conta.');
      return;
    }

    try {
      setDeleteAccountSubmitting(true);
      const response = await base44.functions.invoke('request-account-deletion', {
        accountDeletionPageUrl: getAccountDeletionUrl(),
      });

      toast.success(response?.data?.message || 'Conta excluida com sucesso.');
      try {
        await base44.auth.logout();
      } catch (logoutError) {
        console.warn('[account-deletion] logout apos exclusao falhou', logoutError);
      }
      if (typeof window !== 'undefined') {
        window.location.href = `${getAccountDeletionUrl()}?status=deleted`;
      }
    } catch (error) {
      console.error('[account-deletion] falha ao excluir conta', error);
      toast.error(error?.message || 'Nao foi possivel excluir a conta.');
    } finally {
      setDeleteAccountSubmitting(false);
    }
  };

  const retryProfileLoading = async () => {
    setProfileLoadTimedOut(false);
    await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    await queryClient.invalidateQueries({ queryKey: ['memberData'] });
  };

  const handleAuthSubmit = async () => {
    if (authMode !== 'update') {
      if (!email) {
        toast.error('Informe seu e-mail.');
        return;
      }
      if (!isValidEmailAddress(email)) {
        toast.error('Informe um e-mail valido.');
        return;
      }
    }

    if (authMode !== 'reset' && authMode !== 'update' && !password) {
      toast.error('Informe sua senha.');
      return;
    }

    if (authMode === 'update') {
      if (!newPassword || !confirmPassword) {
        toast.error('Informe e confirme a nova senha.');
        return;
      }
      if (newPassword.length < 6) {
        toast.error('A nova senha deve ter pelo menos 6 caracteres.');
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error('As senhas não conferem.');
        return;
      }
    }

    try {
      setAuthSubmitting(true);

      if (authMode === 'update') {
        await base44.auth.updatePassword(newPassword);
        toast.success('Senha atualizada com sucesso. Faça login com a nova senha.');
        await base44.auth.logout();
        setAuthMode('login');
        if (typeof window !== 'undefined') {
          if (isNativePlatform()) {
            navigate(createPageUrl('Perfil'), { replace: true });
          } else {
            window.location.href = `${window.location.origin}${createPageUrl('Perfil')}`;
          }
        }
        return;
      }

      if (authMode === 'reset') {
        await base44.auth.resetPassword(
          email,
          getRecoveryRedirectUrl()
        );
        toast.success('Link de redefinição enviado para seu e-mail.');
        setAuthMode('login');
        return;
      }

      if (authMode === 'login') {
        await base44.auth.signIn(email, password);
        toast.success('Login realizado com sucesso.');
        if (isNativePlatform()) {
          navigate(createPageUrl('Perfil'), { replace: true });
        } else {
          window.location.href = createPageUrl('Perfil');
        }
        return;
      }

      await base44.auth.signUp(
        email,
        password,
        `${window.location.origin}${createPageUrl('Perfil')}`
      );
      toast.success('Cadastro realizado. Confirme seu e-mail, se solicitado, e faça login.');
      setAuthMode('login');
    } catch (error) {
      toast.error(error.message || 'Erro ao autenticar.');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handleSocialAuth = async (provider) => {
    try {
      setSocialSubmitting(provider);
      const redirectUrl = getOAuthRedirectUrl();
      await base44.auth.signInWithOAuth(provider, redirectUrl);
    } catch (error) {
      toast.error(error.message || 'Erro ao autenticar com provedor social.');
      setSocialSubmitting('');
    }
  };

  const effectiveMemberData = resolvedMemberData || memberData;

  if ((checkingAuth && !isLoggedIn && !currentUser) || (isLoggedIn && loadingMemberData && !effectiveMemberData)) {
    if (profileLoadTimedOut) {
      return (
        <div className="min-h-screen bg-gray-50 pb-24">
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-20 px-4">
            <div className="max-w-lg mx-auto">
              <PageHeader title="Perfil" showBack={true} />
            </div>
          </div>

          <div className="max-w-lg mx-auto px-4 -mt-14">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-lg p-6 text-center"
            >
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Perfil demorou para carregar</h2>
              <p className="text-gray-600 mb-6">
                A autenticacao foi iniciada, mas o carregamento do perfil demorou mais do que o esperado.
              </p>
              {currentUser?.email && (
                <p className="text-sm text-gray-500 mb-6">
                  Conta atual: {currentUser.email}
                </p>
              )}
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={retryProfileLoading}>
                Tentar novamente
              </Button>
              <Button
                variant="outline"
                className="w-full mt-3"
                onClick={async () => {
                  await base44.auth.logout();
                  navigate(createPageUrl('Perfil'), { replace: true });
                }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </motion.div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isLoggedIn || authMode === 'update') {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-20 px-4">
          <div className="max-w-lg mx-auto">
            <PageHeader title="Perfil" showBack={true} />
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 -mt-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-xl border border-indigo-50 p-6 text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-10 h-10 text-indigo-700" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Acesse seu perfil</h2>
            <p className="text-gray-600 mb-6">
              {authMode === 'update'
                ? 'Defina sua nova senha para concluir a recuperação da conta.'
                : 'Entre na sua conta para visualizar suas informações e acessar os recursos.'}
            </p>
            {authMode !== 'update' && lastNativeAuthError ? (
              <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-3 text-left">
                <p className="text-sm font-semibold text-red-700">Falha no login social</p>
                <p className="mt-1 text-sm text-red-600">{lastNativeAuthError}</p>
              </div>
            ) : null}
            {authMode !== 'update' && (
              <div className="flex rounded-xl border border-gray-200 p-1 mb-5 bg-gray-50">
                <button
                  type="button"
                  className={`flex-1 py-2.5 text-sm rounded-lg transition ${authMode === 'login' ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                  onClick={() => setAuthMode('login')}
                >
                  Entrar
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2.5 text-sm rounded-lg transition ${authMode === 'signup' ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                  onClick={() => setAuthMode('signup')}
                >
                  Cadastrar
                </button>
              </div>
            )}
            <div className="space-y-3 text-left">
              {authMode !== 'update' && (
                <Input
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
              {authMode !== 'reset' && authMode !== 'update' && (
                <Input
                  type="password"
                  placeholder="Sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
              {authMode === 'update' && (
                <>
                  <Input
                    type="password"
                    placeholder="Nova senha"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <Input
                    type="password"
                    placeholder="Confirmar nova senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </>
              )}
              {authMode === 'login' && (
                <button
                  type="button"
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                  onClick={() => setAuthMode('reset')}
                >
                  Esqueci minha senha
                </button>
              )}
              {authMode === 'reset' && (
                <button
                  type="button"
                  className="text-sm text-indigo-600 hover:text-indigo-700"
                  onClick={() => setAuthMode('login')}
                >
                  Voltar para login
                </button>
              )}
              <Button
                onClick={handleAuthSubmit}
                disabled={authSubmitting}
                className="w-full h-11 bg-indigo-600 hover:bg-indigo-700"
              >
                <User className="w-4 h-4 mr-2" />
                {authSubmitting
                  ? 'Aguarde...'
                  : (authMode === 'login'
                    ? 'Entrar'
                    : authMode === 'signup'
                    ? 'Criar Conta'
                    : authMode === 'update'
                    ? 'Salvar Nova Senha'
                    : 'Enviar Link')}
              </Button>
              {authMode !== 'update' && (
                <>
                  <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-3 text-gray-500">ou continue com</span>
                    </div>
                  </div>
                  <div className={`grid gap-2 ${showAppleLogin ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!!socialSubmitting}
                      onClick={() => handleSocialAuth('google')}
                      className="w-full h-11"
                    >
                      <Chrome className="w-4 h-4 mr-2" />
                      {socialSubmitting === 'google' ? 'Conectando...' : 'Google'}
                    </Button>
                    {showAppleLogin && (
                      <Button
                        type="button"
                        variant="default"
                        disabled={!!socialSubmitting}
                        onClick={() => handleSocialAuth('apple')}
                        className="w-full h-11 bg-black text-white hover:bg-black/90"
                      >
                        {socialSubmitting === 'apple' ? 'Conectando...' : 'Apple'}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!effectiveMemberData) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-20 px-4">
          <div className="max-w-lg mx-auto">
            <PageHeader title="Perfil" showBack={true} />
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 -mt-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 text-center"
          >
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Acesso restrito</h2>
            <p className="text-gray-600 mb-6">
              Esta conta esta autenticada, mas ainda nao existe um cadastro de membro vinculado a este e-mail.
            </p>
            {currentUser?.email && (
              <p className="text-sm text-gray-500 mb-6">
                Conta atual: {currentUser.email}
              </p>
            )}
            <Link to={createPageUrl('CadastroMembro')}>
              <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                <Edit className="w-4 h-4 mr-2" />
                Concluir Cadastro de Membro
              </Button>
            </Link>
            <Button
              variant="outline"
              className="w-full mt-3"
              onClick={async () => {
                await base44.auth.logout();
                navigate(createPageUrl('Perfil'), { replace: true });
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair e trocar conta
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  const initials = currentUser?.full_name
    ? currentUser.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : 'U';

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-20 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Perfil" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-14">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-4 sm:p-6"
        >
          <div className="flex items-center gap-3 sm:gap-4">
            <Avatar className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
              <AvatarImage src={effectiveMemberData?.photo_url} />
              <AvatarFallback className="bg-indigo-100 text-indigo-600 text-lg sm:text-xl font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-xl font-bold text-gray-900 truncate">{currentUser?.full_name}</h2>
              {effectiveMemberData?.status && (
                <span className={`inline-block mt-1.5 sm:mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                  effectiveMemberData.status === 'Ativo' 
                    ? 'bg-green-100 text-green-700' 
                    : effectiveMemberData.status === 'Pendente'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {effectiveMemberData.status}
                </span>
              )}
            </div>
          </div>

          {effectiveMemberData ? (
            <Link to={createPageUrl('EditarPerfil')}>
              <Button className="w-full mt-3 sm:mt-4 bg-indigo-600 hover:bg-indigo-700 text-sm sm:text-base">
                <Edit className="w-4 h-4 mr-2" />
                Editar Dados
              </Button>
            </Link>
          ) : (
            <Link to={createPageUrl('CadastroMembro')}>
              <Button className="w-full mt-3 sm:mt-4 bg-indigo-600 hover:bg-indigo-700 text-sm sm:text-base">
                <Edit className="w-4 h-4 mr-2" />
                Completar Cadastro
              </Button>
            </Link>
          )}
        </motion.div>

        {/* Menu Options */}
        <div className="mt-6 space-y-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm overflow-hidden"
          >
            <Link to={createPageUrl('PedidoOracao')}>
              <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Pedido de Oração</span>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
              </div>
            </Link>

            <Link to={createPageUrl('Mensagens')}>
              <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Comunicados</span>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
              </div>
            </Link>

            <Link to={createPageUrl('Grupos')}>
              <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-sky-600" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Ministérios</span>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
              </div>
            </Link>

            <Link to={createPageUrl('CalendarioIntegrado')}>
              <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Calendário Integrado</span>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
              </div>
            </Link>

            {hasRankingMBVAccess && (
              <Link to={createPageUrl('RankingMBV')}>
                <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-700" />
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium text-sm sm:text-base text-gray-900 truncate block">Ranking MBV</span>
                      <span className="text-xs text-gray-500">Pontuação e check-in</span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>
            )}

            <Link to={createPageUrl('CartaoMembro')}>
              <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-medium text-sm sm:text-base text-gray-900 truncate block">Cartão de Membro</span>
                    {effectiveMemberData?.member_card_enabled !== true && (
                      <span className="text-xs text-gray-500">Acesso mediante liberação</span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
              </div>
            </Link>

            <Link to={createPageUrl('Biblia')}>
              <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Bíblia</span>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
              </div>
            </Link>

            <Link to={createPageUrl('EquipeLouvor')}>
              <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Music className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Equipe de Louvor</span>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
              </div>
            </Link>
            <Link to={createPageUrl('EquipeMidia')}>
                <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Mídia</span>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>

            <Link to={createPageUrl('EquipeDanca')}>
                <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-fuchsia-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Music className="w-4 h-4 sm:w-5 sm:h-5 text-fuchsia-600" />
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Dança MBV</span>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>

            <Link to={createPageUrl('StaffCulto')}>
                <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700" />
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Staff do Culto</span>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>

            <Link to={createPageUrl('EquipeRecepcao')}>
                <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Handshake className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Recepção</span>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>

            <Link to={createPageUrl('EquipeKidsMBV')}>
                <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Baby className="w-4 h-4 sm:w-5 sm:h-5 text-pink-600" />
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Kids MBV</span>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>
          </motion.div>

          {/* Admin Options */}
          {isAdmin && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm overflow-hidden"
            >
              <div className="px-4 py-2 bg-gray-50 border-b">
                <span className="text-xs font-medium text-gray-500 uppercase">Administração</span>
              </div>

              <Link to={createPageUrl('AdminDashboard')}>
                <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Painel Administrativo</span>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>

              <Link to={createPageUrl('Relatorios')}>
                <div className="flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors border-t">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-lg sm:text-xl">📊</span>
                    </div>
                    <span className="font-medium text-sm sm:text-base text-gray-900 truncate">Relatórios</span>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl shadow-sm overflow-hidden"
          >
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between p-3 sm:p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-sm sm:text-base text-gray-900">Excluir conta</div>
                      <div className="text-xs sm:text-sm text-gray-500">Inicie a exclusao definitiva da sua conta e dados</div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir conta</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acao exclui permanentemente a conta vinculada a {currentUser?.email || 'este acesso'} e remove os dados diretamente associados a ela. Essa acao nao pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleAccountDeletionRequest}
                    disabled={deleteAccountSubmitting}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {deleteAccountSubmitting ? 'Excluindo...' : 'Excluir conta agora'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </motion.div>

          {/* Logout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              variant="outline"
              className="w-full text-red-600 border-red-200 hover:bg-red-50 text-sm sm:text-base"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair da Conta
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
